import { curryReact } from "@/utils/curryReact";
import { useDeepCompareEffect } from "ahooks";
import { isEqual, omitBy, pickBy } from "lodash";
import React, {
  forwardRef,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";

type ComponentsProps<T> = T extends keyof JSX.IntrinsicElements
  ? JSX.IntrinsicElements[T]
  : T extends ComponentType<infer P>
  ? P
  : never;

type TrackerProps<T> = {
  component: T;
  children?: ReactNode;
} & ComponentsProps<T>;

const trackerProps = new Set([
  "data-gd-expose",
  "data-gd-click",
  "data-button-name",
  "data-content-source",
  "data-content-id",
  "data-board-id",
  "data-material-id",
  "data-ext",
  "data-file-id",
  "data-topic-id",
  "data-unique-id",
  "data-dialog-title",
  "data-content-type",

  "data-resource-title",
  "data-resource-id",
  "data-subject-name",
  "data-plan-id",
  "data-plan-name",
  "data-item-location",
  "data-resource-location",
  "data-resource-func",
  "data-resource-type",

  "data-topic-id",
  "data-topic-title",

  "data-recommend-search-word",
  "data-recommend-search-word-type",
  "data-recommend-search-word-location",
]);
/**
 * 埋点组件，主要功能用于异步渲染埋点相关属性，来减少服务端渲染内容大小
 * @example <Tracker component={“a” as const} href="/test" data-gd-click="button-click" data-button-name="测试按钮">测试按钮</Tracker>
 * @example // 自定义组件
 * function CustomComponent(props) {
 *    // ...业务代码
 *    return <div {...props} />;
 * }
 *
 * <Tracker component={CustomComponent} data-gd-click="button-click" data-button-name="测试组件">测试组件</Tracker>
 */
const Tracker: <T>(props: TrackerProps<T>) => JSX.Element = (forwardRef as any)(
  function <P>({ component, children, ...props }: TrackerProps<P>, ref) {
    const [trackProps, setTrackProps] = useState({});

    const normalProps = useMemo(() => {
      return omitBy(props, (value, key) => trackerProps.has(key));
    }, [props]);

    useDeepCompareEffect(() => {
      const newTrackProps = pickBy(props, (value, key) =>
        trackerProps.has(key)
      );

      if (isEqual(trackProps, newTrackProps)) {
        return;
      }

      setTrackProps(newTrackProps);
    }, [props]);

    return React.createElement(
      component as keyof JSX.IntrinsicElements | ComponentType,
      {
        ref,
        ...normalProps,
        ...trackProps,
      } as P,
      children
    );
  }
);

export default Tracker;

/** 与div标签一致，只多了一层埋点属性的异步渲染处理 */
export const TrackerDiv: React.FC<JSX.IntrinsicElements["div"]> = curryReact(
  Tracker,
  {
    component: "div",
  } as never
);

/** 与a标签一致，只多了一层埋点属性的异步渲染处理 */
export const TrackerA: React.FC<JSX.IntrinsicElements["a"]> = curryReact(
  Tracker,
  {
    component: "a",
  } as never
);
